import {
  KalturaPersonalListListResponse,
  PersonalListListAction,
  KalturaPersonalListFilter,
  KalturaFilterPager,
  PersonalListDeleteAction,
  PersonalListAddAction,
  KalturaPersonalList,
  AssetListPersonalSelectionAction,
  AssetPersonalSelectionUpsertAction,
  KalturaPersonalAssetSelectionFilter,
  KalturaAssetType,
  KalturaPersonalAssetSelectionOrderBy,
  KalturaProgramAsset,
} from '@kaltura-ott/ott-client-types/api/types';
import { Observable, of, concat, throwError } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';

import { KuxMediaAssetType, KuxProgramType } from '../interfaces';

import { getAssetHistoryList, getMediaAssetsByAssetHistory } from './asset';
import { assetToType } from './asset-v2.helper';
import { assetToProgram } from './asset.helper';
import { kalturaClient } from './kaltura-clent';

interface GetPersonalList {
  partnerListTypes: number[];
  pageIndex?: number;
  pageSize?: number;
  orderBy?: string;
}
export const getPersonalList = ({
  partnerListTypes,
  pageIndex = 1,
  pageSize = 50,
  orderBy,
}: GetPersonalList): Observable<KalturaPersonalListListResponse> => {
  const action = new PersonalListListAction({
    filter: new KalturaPersonalListFilter({
      partnerListTypeIn: partnerListTypes.join(','),
      ...(orderBy ? { orderBy: orderBy } : {}),
    }),
    pager: new KalturaFilterPager({
      pageIndex,
      pageSize,
    }),
  });
  const client = kalturaClient();
  return client.request(action).pipe(
    catchError((error) => {
      return throwError(error);
    }),
  );
};

interface DeletePersonalList {
  id: number;
}

export const deletePersonalList = ({ id }: DeletePersonalList) => {
  const action = new PersonalListDeleteAction({ personalListId: id });
  const client = kalturaClient();
  return client.request(action).pipe(
    catchError((error) => {
      return of(error);
    }),
  );
};

interface DeleteAllPersonalList {
  partnerListId: number;
}
export const deleteAllPersonalList = ({ partnerListId }: DeleteAllPersonalList) => {
  return getPersonalList({ partnerListTypes: [partnerListId] }).pipe(
    switchMap((list) => {
      if (!list.objects || !list.objects.length) {
        return of([]);
      }
      return concat(...list.objects.map((item) => deletePersonalList({ id: item.id })));
    }),
  );
};

const escapeKsqlValue = (value: string): string => {
  let newValue = value;
  const replacement = [
    ["'", '%27'],
    ['"', '%22'],
  ];
  replacement.forEach(([from, to]) => {
    newValue = newValue.replace(new RegExp(from, 'g'), to);
  });
  return newValue;
};

export const addPersonalList = (asset: KuxProgramType, recommendationFields: string[], partnerListType: number) => {
  const name = `saved_recomendation_item_${asset.id}`;
  const recommendationString = `${recommendationFields
    .map((key) => asset.tags[key]?.map((value) => `${key}='${escapeKsqlValue(value)}'`).join(' '))
    .filter(Boolean)
    .join(' ')}`;
  if (!recommendationString) {
    return;
  }
  const ksql = `(or ${recommendationString})`;
  const action = new PersonalListAddAction({
    personalList: new KalturaPersonalList({ partnerListType: partnerListType, name, ksql }),
  });
  const client = kalturaClient();
  return client.request(action).pipe(
    catchError((error) => {
      return of(error);
    }),
  );
};

interface AddFullPersonalList {
  partnerListId: number;
  recommendationFields: string[];
  typeArr: string[];
  suggestedCalculationByLastXItems: number;
}

/* TODO: use Bookmark API if it possible */
export const addFullPersonalList = ({
  partnerListId,
  recommendationFields,
  typeArr,
  suggestedCalculationByLastXItems,
}: AddFullPersonalList) => {
  return getAssetHistoryList({ pageSize: suggestedCalculationByLastXItems, typeIn: typeArr.join(',') }).pipe(
    mergeMap((assetHistoryList) => {
      return getMediaAssetsByAssetHistory(assetHistoryList.objects).pipe(
        switchMap((assetsResponse) => {
          const assets: KuxProgramType[] =
            assetsResponse.objects?.map((asset) => assetToProgram(asset as KalturaProgramAsset) as KuxProgramType) ||
            [];
          return concat(
            // @ts-expect-error TODO Fix
            ...assets.map((asset) => addPersonalList(asset, recommendationFields, partnerListId)).filter(Boolean),
          );
        }),
      );
    }),
  );
};

export const getSearchHistoryList = <T extends KuxMediaAssetType>(slotNumberEqual?: number): Observable<T[]> => {
  if (!slotNumberEqual) {
    return of([]);
  }
  const action = new AssetListPersonalSelectionAction({
    filter: new KalturaPersonalAssetSelectionFilter({
      slotNumberEqual,
      orderBy: KalturaPersonalAssetSelectionOrderBy.assetSelectionDateDesc,
    }),
  });
  const client = kalturaClient();
  return client.request(action).pipe(
    map((res) => {
      const assets = res.objects;
      return assets.map((asset) => assetToType(asset)).filter(Boolean) as T[];
    }),
    catchError((error) => {
      return of(error);
    }),
  );
};

export const setSearchHistoryList = (assetId: number, assetType: KalturaAssetType, slotNumber?: number) => {
  if (!slotNumber) {
    return of();
  }

  const action = new AssetPersonalSelectionUpsertAction({ assetId, assetType, slotNumber });
  const client = kalturaClient();
  return client.request(action).pipe(
    catchError((error) => {
      return of(error);
    }),
  );
};
